import { useEffect, useMemo, useState } from 'react'
import { Plus, Undo2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EmptyRow, ErpTable, Td, Th } from '@/components/erp/primitives'
import { CursorPager } from '@/shared/ui/CursorPager'
import { Modal, Field } from '@/shared/ui/Modal'
import { ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useCan } from '@/shared/auth/session'
import { dayKey, fmtDate, fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type {
  BeneficiaryResponse, DisbursementResponse, ProgramResponse, SpendResponse,
} from '@/shared/api/types'
import {
  getSpend, listBeneficiaries, listDisbursements, reconcileSpend, recordDisbursement,
  reverseDisbursement,
} from '../api'
import { Money, UnattributedNote } from '../parts'
import { CHANNEL_ORDER, EntryTypeBadge, REVERSAL_REASON_ORDER, channelLabel, reversalReasonLabel } from '../vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Money: the ledger, and the one derived total.
 *
 * ## The ledger is append-only. A correction is a reversal, not an edit.
 *
 * There is no edit control and no delete control on this screen, and there is no
 * endpoint behind one. A mistake leaves TWO rows — what was recorded and the
 * correction — and the sum is still right. A ledger you can edit is a ledger
 * nobody can audit.
 *
 * ## One number for spend, derived
 *
 * `spentAmount` is a trigger-maintained rollup of these rows. PollSphere carried
 * five money columns — budget, spent, disbursed, committed, remaining — that no
 * code kept in agreement, so the programme card, the money tab and the ROI chart
 * each showed a different total.
 *
 * ## A reversal reverses money, not history
 *
 * The beneficiary's delivery still happened and their status does not move.
 * Revoking a delivery is a separate decision with its own reason.
 */
export function MoneyPanel({ program, onChanged }: {
  program: ProgramResponse; onChanged?: () => void
}) {
  const canDisburse = useCan('program:disburse')
  const canUpdate = useCan('program:update')

  const [spend, setSpend] = useState<SpendResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)
  const [reversing, setReversing] = useState<DisbursementResponse | null>(null)
  const [busy, setBusy] = useState(false)

  const filters = useMemo(() => ({}), [])
  const page = useCursorPage<DisbursementResponse>(
    (cursor) => listDisbursements(program.id, filters, cursor),
    [program.id],
  )

  const loadSpend = () => getSpend(program.id).then(setSpend).catch((e) => setError(explain(e)))

  useEffect(() => {
    let alive = true
    getSpend(program.id)
      .then((s) => { if (alive) setSpend(s) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [program.id])

  const reconcile = async () => {
    setBusy(true)
    try { setSpend(await reconcileSpend(program.id)) }
    catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  const rows = page.rows

  return (
    <div className="space-y-4">
      <ErrorStrip message={error} />

      {!program.currency && (
        <Note tone="bad">
          This programme has no currency set. Spend cannot be recorded until it does — an amount
          with no currency is not a quantity of anything.
        </Note>
      )}

      {spend && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Budget" value={<Money value={spend.budgetAmount} currency={spend.currency} />} />
          <Stat label="Spent" value={<Money value={spend.spentAmount} currency={spend.currency} />}
            sub="derived from the ledger" />
          <Stat
            label="Remaining"
            value={spend.remaining === null || spend.remaining === undefined
              ? <span className="text-muted-foreground text-erp-sm">—</span>
              : <Money value={spend.remaining} currency={spend.currency} />}
            sub={spend.remaining === null || spend.remaining === undefined
              ? 'no budget recorded' : undefined}
          />
          <Stat
            label="Entries"
            value={<span className="tabular-nums">{fmtNumber(spend.entryCount)}</span>}
            sub={spend.reversalCount
              ? `${fmtNumber(spend.reversalCount)} reversal${spend.reversalCount === 1 ? '' : 's'}`
              : undefined}
          />
        </div>
      )}

      {spend && (
        <div className="rounded-md border border-border p-3 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-erp-sm font-medium">Reconciliation</h4>
            {canUpdate && (
              <button type="button" className="erp-btn h-7 text-erp-xs" onClick={reconcile} disabled={busy}>
                Reconcile now
              </button>
            )}
          </div>
          <p className="text-erp-xs text-muted-foreground">
            Ledger sum <Money value={spend.ledgerSum} currency={spend.currency} /> against the
            derived rollup <Money value={spend.spentAmount} currency={spend.currency} />.
            {spend.reconciledAt && ` Last checked ${fmtDateTime(spend.reconciledAt)}.`}
          </p>
          {spend.withinTolerance ? (
            <p className="text-erp-xs text-emerald-700 dark:text-emerald-300">Within tolerance.</p>
          ) : (
            <p className="text-erp-xs text-red-700 dark:text-red-300">
              Discrepancy of <Money value={spend.discrepancy} currency={spend.currency} precise /> — the
              rollup and the ledger disagree, and one of them is wrong.
            </p>
          )}
          <UnattributedNote
            amount={spend.unattributedAmount}
            currency={spend.currency}
            count={spend.unattributedCount}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <h4 className="text-erp-sm font-medium">Ledger</h4>
        {canDisburse && (
          <button type="button" className="erp-btn h-7 text-erp-xs"
            disabled={!program.currency} onClick={() => setRecording(true)}>
            <Plus className="h-3.5 w-3.5" /> Record
          </button>
        )}
      </div>

      <ErrorStrip message={page.error} />
      <ErpTable head={(
        <>
          <Th>Type</Th>
          <Th>Beneficiary</Th>
          <Th>Channel</Th>
          <Th>Date</Th>
          <Th className="text-right">Amount</Th>
          <Th />
        </>
      )}>
        {page.loading && rows.length === 0 ? <LoadingRows colSpan={6} />
          : rows.length === 0 ? <EmptyRow colSpan={6} message="Nothing recorded yet." />
          : rows.map((d) => {
            // Only the CURRENT page is visible here, so a reversal that landed
            // on the next page cannot be seen. `DisbursementResponse` carries no
            // `reversed` flag, so this cannot be answered correctly client-side:
            // what it can say is "no reversal on this page", which is why the
            // button is disabled rather than offered on the strength of a
            // negative it cannot establish.
            const reversedHere = rows.some((x) => x.reversesId === d.id)
            // `!page.hasMore` says only that nothing follows THIS page. A
            // reversal is created after its original, so with a newest-first
            // ledger it is specifically the row most likely to sit on an EARLIER
            // page — the one case `hasMore` cannot see. The whole ledger has to
            // fit one page for the absence of a reversal to mean anything.
            const reversalKnown = reversedHere || (page.page === 0 && !page.hasMore)
            const reversed = reversedHere
            return (
              <tr key={d.id} className="erp-row">
                <Td><EntryTypeBadge code={d.entryTypeCode} /></Td>
                <Td className="text-erp-xs">
                  {d.beneficiaryLabel ?? (
                    // A row with no beneficiary is a real case — a village water
                    // tank. Every per-person cost figure excludes it and says so.
                    <span className="text-muted-foreground">not attributed to a person</span>
                  )}
                  {d.reversalReasonCode && (
                    <div className="text-muted-foreground">
                      {reversalReasonLabel(d.reversalReasonCode)}
                    </div>
                  )}
                </Td>
                <Td className="text-erp-xs">{channelLabel(d.channelCode)}</Td>
                <Td className="text-erp-xs whitespace-nowrap">{fmtDate(d.occurredOn)}</Td>
                <Td className="text-right">
                  <span className={Number(d.amount) < 0 ? 'text-red-700 dark:text-red-300' : ''}>
                    <Money value={d.amount} currency={d.currency} />
                  </span>
                </Td>
                <Td>
                  {canDisburse && d.entryTypeCode === 'DISBURSEMENT' && !reversed && (
                    <button
                      type="button"
                      className={cn('erp-btn-ghost h-7 text-erp-xs', !reversalKnown && 'opacity-50')}
                      onClick={() => setReversing(d)}
                      disabled={!reversalKnown}
                      title={reversalKnown
                        ? undefined
                        : 'There are more ledger entries than this page shows, so whether this was already reversed cannot be established from here. Page to the end first.'}
                    >
                      <Undo2 className="h-3.5 w-3.5" /> Reverse
                    </button>
                  )}
                  {reversed && <span className="text-erp-xs text-muted-foreground">reversed</span>}
                </Td>
              </tr>
            )
          })}
      </ErpTable>
      <CursorPager
        page={page.page} count={rows.length} hasMore={page.hasMore}
        canGoBack={page.canGoBack} onBack={page.back} onNext={page.next}
        loading={page.loading} unit="entries"
      />

      <p className="text-erp-xs text-muted-foreground">
        Append-only. There is no edit and no delete — a correction is a reversal, which leaves both
        rows in place so the sum stays auditable. Recording here moves no money; the payment rail
        arrives in a later phase.
      </p>

      {recording && (
        <RecordDialog
          program={program}
          onClose={() => setRecording(false)}
          onSaved={() => { setRecording(false); page.reload(); loadSpend(); onChanged?.() }}
        />
      )}

      {reversing && (
        <ReverseDialog
          entry={reversing}
          onClose={() => setReversing(null)}
          onSaved={() => { setReversing(null); page.reload(); loadSpend(); onChanged?.() }}
        />
      )}
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-erp-xs text-muted-foreground">{label}</div>
      <div className="text-erp-lg font-semibold">{value}</div>
      {sub && <div className="text-erp-xs text-muted-foreground">{sub}</div>}
    </div>
  )
}

function RecordDialog({ program, onClose, onSaved }: {
  program: ProgramResponse; onClose: () => void; onSaved: () => void
}) {
  const [amount, setAmount] = useState('')
  // The hint below has always said "leave the beneficiary unset for something
  // delivered to a place rather than a person" — while there was no beneficiary
  // field at all, so every entry recorded here was unattributed and could never
  // reach a per-person cost figure.
  const [beneficiaryId, setBeneficiaryId] = useState('')
  const [people, setPeople] = useState<BeneficiaryResponse[]>([])
  const [channelCode, setChannel] = useState(program.deliveryChannelCode ?? 'MOBILE_MONEY')
  // `toISOString().slice(0, 10)` is UTC, and Nairobi is UTC+3 — so between
  // midnight and 03:00 it defaults to YESTERDAY, into an append-only ledger with
  // no edit. `dayKey` formats in the campaign's timezone.
  const [occurredOn, setOccurredOn] = useState(dayKey(new Date().toISOString()))
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    // Enrolled and delivered only: you cannot pay somebody who was never enrolled,
    // and a dropped beneficiary is a correction rather than a new payment.
    listBeneficiaries(program.id, { status: 'ENROLLED' }, undefined, 200)
      .then((page) => { if (alive) setPeople(page.content) })
      .catch(() => { if (alive) setPeople([]) })
    return () => { alive = false }
  }, [program.id])

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      await recordDisbursement(program.id, {
        amount: Number(amount), currency: program.currency, channelCode, occurredOn,
        note: note || null,
      })
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="Record a disbursement" footer={(
      <>
        <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="erp-btn-primary" onClick={submit}
          disabled={busy || !amount || Number(amount) <= 0}>
          {busy ? 'Recording…' : 'Record'}
        </button>
      </>
    )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Note tone="info">
          This records what the campaign says it paid. It moves no money — the payment rail
          arrives in a later phase, and this row will carry its reference when it does.
        </Note>
        <Field label="Who"
          hint="Leave unset for something delivered to a place rather than a person — a village water tank. Unattributed spend is excluded from every per-person cost figure, and each of those figures says how much it excluded.">
          <select className="erp-input" value={beneficiaryId}
            onChange={(e) => setBeneficiaryId(e.target.value)}>
            <option value="">Nobody in particular</option>
            {people.map((b) => (
              <option key={b.id} value={b.id}>{b.displayName ?? b.id.slice(0, 8)}</option>
            ))}
          </select>
        </Field>
        <Field label={`Amount (${program.currency ?? '—'})`} required>
          <input className="erp-input" type="number" min={0} step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Channel">
          <select className="erp-input" value={channelCode ?? ''}
            onChange={(e) => setChannel(e.target.value as typeof channelCode)}>
            {CHANNEL_ORDER.map((c) => <option key={c} value={c}>{channelLabel(c)}</option>)}
          </select>
        </Field>
        <Field label="Occurred on" required>
          <DatePicker value={occurredOn} onChange={setOccurredOn} />
        </Field>
        <Field label="Note" hint="Leave the beneficiary unset for something delivered to a place rather than a person — a tank, a shed.">
          <input className="erp-input" value={note} maxLength={512}
            onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

function ReverseDialog({ entry, onClose, onSaved }: {
  entry: DisbursementResponse; onClose: () => void; onSaved: () => void
}) {
  const [reasonCode, setReason] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setError(null)
    try { await reverseDisbursement(entry.id, reasonCode, note || undefined); onSaved() }
    catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="Reverse this entry" footer={(
      <>
        <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="erp-btn-primary" onClick={submit} disabled={busy || !reasonCode}>
          {busy ? 'Reversing…' : 'Reverse'}
        </button>
      </>
    )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Note tone="info">
          This writes a <strong>second row</strong> for{' '}
          <Money value={-Number(entry.amount)} currency={entry.currency} />. The original stays
          exactly as it is — that is what makes the ledger auditable. The beneficiary's delivery
          still happened and their status does not change.
        </Note>
        <Field label="Reason" required>
          <select className="erp-input" value={reasonCode} onChange={(e) => setReason(e.target.value)}>
            <option value="">Choose a reason…</option>
            {REVERSAL_REASON_ORDER.map((r) => (
              <option key={r} value={r}>{reversalReasonLabel(r)}</option>
            ))}
          </select>
        </Field>
        <Field label="Note">
          <input className="erp-input" value={note} maxLength={512}
            onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
